import {HStack, VStack, Text, Icon, Button, Flex, Input} from "@chakra-ui/react";
import {FaRegEdit} from "react-icons/fa";
import dayjs from "dayjs";
import {HiOutlinePlus, HiOutlineTrash, HiOutlineDocumentDuplicate, HiOutlineArrowDownTray} from "react-icons/hi2";
import IntentListItem from "@/pages/scenario/components/IntentListItem";
import CreateIntentDrawer from "@/pages/scenario/components/CreateIntentDrawer";
import {useEffect, useState} from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import ConfirmationButton from "@/components/ui/foundation/ConfirmationButton";
import ScenarioService from "@/services/agency/ScenarioService";
import {useNavigate, useParams} from "react-router"
import {Spinner} from "@chakra-ui/react"
import {showToast, toaster} from "@/components/ui/toaster";
import Mapper from "@/services/utilities/Mapper";
import IntentService from "@/services/agency/IntentService";
import {useChat} from "@/components/providers/ChatProvider";
import {sumMaxScore, scoringConfigToApi, SCORING_ENABLED} from "@/scoringUi";
import {collectScoringValidationErrors} from "@/services/scoringValidation";
import ProductLineTypeSelect from "@/components/ui/selects/ProductLineTypeSelect";
import ResponsibleUserSelect from "@/components/ui/scenarios/ResponsibleUserSelect";
import UserService from "@/services/UserService";

const ScenarioPage = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [scenario, setScenario] = useState(null);
    const [intents, setIntents] = useState([]);
    const [isEditingName, setIsEditingName] = useState(false);
    const [editedName, setEditedName] = useState('');
    const [editedType, setEditedType] = useState(null);
    let params = useParams();
    const [selectedIntent, setSelectedIntent] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isWorking, setIsWorking] = useState(false);
    const [responsibleUsers, setResponsibleUsers] = useState([]);
    const [responsibleUsersLoading, setResponsibleUsersLoading] = useState(true);
    const [isSavingResponsible, setIsSavingResponsible] = useState(false);
    const navigate = useNavigate();
    const { startNewChat } = useChat();

    const fetchScenario = () => {
        if (params.scenarioId) {
            ScenarioService.getById(params.scenarioId).then((scenario) => {
                setScenario(scenario);
            })
        }
    }


    useEffect(() => {
        fetchScenario();
    }, [params]);

    useEffect(() => {
        UserService.getAll({ pageSize: 200 })
            .then((res) => setResponsibleUsers(res?.items ?? []))
            .catch(() => setResponsibleUsers([]))
            .finally(() => setResponsibleUsersLoading(false));
    }, []);

    const handleResponsibleChange = (userId) => {
        if (!scenario) return;
        const previous = scenario.responsibleUser ?? null;
        const nextUser = userId ? responsibleUsers.find((u) => u.id === userId) ?? null : null;
        setScenario((prev) => ({ ...prev, responsibleUser: nextUser }));
        setIsSavingResponsible(true);
        const action = userId
            ? ScenarioService.setResponsibleUser(scenario.id, userId)
            : ScenarioService.clearResponsibleUser(scenario.id);
        action
            .then(() => showToast("Saved", userId ? "Responsible person updated" : "Responsible person cleared", "success"))
            .catch((err) => {
                setScenario((prev) => ({ ...prev, responsibleUser: previous }));
                showToast("Error", err?.response?.data?.error ?? "Could not update the responsible person", "error");
            })
            .finally(() => setIsSavingResponsible(false));
    };

    useEffect(() => {
        if (scenario?.intents) setIntents(scenario.intents);
    }, [scenario]);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = intents.findIndex(i => i.id === active.id);
        const newIndex = intents.findIndex(i => i.id === over.id);
        const reordered = arrayMove(intents, oldIndex, newIndex);
        setIntents(reordered);
        ScenarioService.reorderIntents(scenario.id, reordered.map(i => i.id));
    };

    const handleIntentClick = (intent) => {
        setSelectedIntent(intent);
        setIsDrawerOpen(true);
    }

    const handleAddNewIntent = () => {
        setSelectedIntent(null);
        setIsDrawerOpen(true);
    }


    const handleDeleteScenario = () => {
        setIsLoading(true);

        ScenarioService.remove(scenario.id).then((response) => {
            if (response.status === 204) {
                showToast("Success", `Scenario "${scenario.name}" deleted successfully`, 'success');
                navigate('/scenarios');
            }
        }).finally(() =>
            setIsLoading(false)
        )
    }

    const handleIntentRemove = (intent) => {
        setIsWorking(true);

        IntentService.remove(intent.id).then(response => {
            if (response.status === 204) {
                setIsDrawerOpen(false);
                showToast("Success", `Intent "${intent.name}" deleted successfully`, 'success');

                fetchScenario();
            }
        }).finally(() =>
            setIsWorking(false)
        );
    }

    const handleOnSave = (intent) => {
        if (SCORING_ENABLED) {
            const otherIntentsEntities = intents.filter((i) => i.id !== intent.id).map((i) => i.entities ?? []);
            const scoringErrors = collectScoringValidationErrors(intent.entities, otherIntentsEntities);
            if (scoringErrors.length) {
                scoringErrors.forEach((message) => toaster.create({ title: "Błąd oceny kryterium", description: message, type: "error" }));
                return;
            }
        }

        if (intent.id) {
            handleIntentUpdate(intent);
        } else {
            handleIntentSave(intent);
        }
    }

    const handleIntentSave = (intent) => {
        setIsWorking(true);

        const mapped = Mapper.mapIntentToApiFormat(
            intent, scenario.id);

        IntentService.addIntentToScenario(mapped, scenario.id).then(response => {

            toaster.create({
                title: "Success",
                description: `Intent "${mapped.name}" created successfully`,
                type: 'success',
            })

            ScenarioService.getById(params.scenarioId).then((scenario) => {
                setScenario(scenario);
            })
        }).finally(() =>
            setIsWorking(false)
        );
    }

    const handleIntentUpdate = (intent) => {
        setIsWorking(true);

        const mapped = Mapper.mapIntentToApiFormat(
            intent, scenario.id);
        

        IntentService.update(intent.id, mapped).then(response => {
            showToast("Success", `Intent "${mapped.name}" updated successfully`, 'success');

            fetchScenario();
        }).finally(() =>
            setIsWorking(false)
        );
    }

    const handleDuplicateScenario = () => {
        setIsLoading(true);
        ScenarioService.duplicate(scenario.id).then((response) => {
            showToast("Success", `Scenario duplicated successfully`, 'success');
            navigate(`/scenarios`);
        }).finally(() => setIsLoading(false));
    }

    const handleEditNameClick = () => {
        setEditedName(scenario.name);
        setEditedType(scenario.type ?? null);
        setIsEditingName(true);
    }

    const handleNameSave = () => {
        if (!editedType) {
            showToast("Error", "Scenario type is required", "error");
            return;
        }
        setIsWorking(true);
        ScenarioService.rename(scenario.id, editedName, editedType).then(() => {
            setScenario(prev => ({ ...prev, name: editedName, type: editedType }));
            setIsEditingName(false);
            showToast("Success", `Scenario renamed to "${editedName}"`, 'success');
        }).finally(() => setIsWorking(false));
    }

    const handleStartNewChat = () => {
        (startNewChat && scenario) && startNewChat(scenario);
    }

    const handleExportScenario = () => {
        const exportData = {
            name: scenario.name,
            intents: (scenario.intents ?? []).map(intent => ({
                name: intent.name,
                displayName: intent.displayName,
                finalResponse: intent.finalResponse,
                position: intent.position,
                trainingPhrases: intent.trainingPhrases,
                entities: (intent.entities ?? []).map(e => ({
                    name: e.name,
                    completeResponse: e.completeResponse,
                    response: e.response,
                    nextIntent: e.nextIntent,
                    prompts: e.prompts,
                    skipPhrases: e.skipPhrases,
                    transitions: e.transitions,
                    dynamicValues: e.dynamicValues,
                    dynamicWorkerValues: e.dynamicWorkerValues,
                    isRequired: e.isRequired,
                    isConditional: e.isConditional,
                    modifyResponses: e.modifyResponses,
                    isFinishing: e.isFinishing,
                    endConversation: e.endConversation,
                    scoring: scoringConfigToApi(e.scoring),
                })),
                dynamicValues: intent.dynamicValues,
                dynamicWorkerValues: intent.dynamicWorkerValues,
            })),
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${scenario.name.replace(/\s+/g, '_')}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    const allCriteria = intents.flatMap((intent) => intent.entities ?? []);
    const scenarioMaxScore = sumMaxScore(allCriteria);

    if (!scenario && params.scenarioId) {
        return (
            <Flex w={'full'} py={24} alignItems={'center'} justifyContent={'center'}>
                <Spinner size={'xl'} color={'white'}/>
            </Flex>
        )
    }

    return (
        <VStack bg={'bg.card'} w={'full'} borderWidth={1} borderRadius={16} pb={4}>
            <HStack w={'100%'} px={4} borderBottomWidth={1} py={4} justifyContent={'space-between'}>
                <HStack gap={4} flex={1}>
                    {isEditingName ? (
                        <>
                            <Input
                                value={editedName}
                                onChange={(e) => setEditedName(e.target.value)}
                                autoFocus
                                size={'md'}
                                color={'white'}
                                w={'30%'}
                            />
                            <ProductLineTypeSelect value={editedType} onChange={setEditedType} label={null} required={false} size={'sm'} />
                            <ConfirmationButton withoutConfirmation variant="primary" label="Save" onConfirm={handleNameSave} isLoading={isWorking} minW={'auto'}/>
                            <Button size={'md'} variant="outlined" onClick={() => setIsEditingName(false)}>Cancel</Button>
                        </>
                    ) : (
                        <>
                            <Text color={'white'} fontSize={15} fontWeight={500}>{scenario?.name}</Text>
                            {scenario?.type && (
                                <Text color={'text.grey'} fontSize={13}>{scenario.type === 'SalesManager' ? 'Sales Manager' : scenario.type}</Text>
                            )}
                            <Icon fontSize={16} color={'text.grey'} cursor={'pointer'} onClick={handleEditNameClick}>
                                <FaRegEdit/>
                            </Icon>
                        </>
                    )}
                </HStack>
                <HStack gap={6}>
                    <HStack gap={2}>
                        <Text color={'text.grey'} fontSize={13}>Responsible:</Text>
                        <ResponsibleUserSelect
                            value={scenario?.responsibleUser?.id ?? null}
                            users={responsibleUsers}
                            loading={responsibleUsersLoading || isSavingResponsible}
                            onChange={handleResponsibleChange}
                            width="200px"
                        />
                    </HStack>
                    <Button
                        variant="outlined"
                        onClick={handleStartNewChat}
                        textDecoration='unset'
                        size={'sm'}
                        fontSize={14}>
                        <Text>Start new chat</Text>
                    </Button>
                    <Text color={'text.grey'}>Last
                        edited: {dayjs(scenario?.editedAtUtc ?? scenario?.modifiedAt).format('DD.MM.YYYY HH:mm')}</Text>
                </HStack>
            </HStack>
            <VStack w={'full'} px={4} gap={6} py={4}>
                <VStack alignItems={'flex-start'} gap={0} w={'full'} px={4}>
                    <Text color={'white'} fontSize={15} fontWeight={500}>Existing intents</Text>
                    <Text color={'white'} fontSize={14} fontWeight={400}>Drag and drop to change the order of the
                        intents</Text>
                </VStack>
                {SCORING_ENABLED && scenarioMaxScore > 0 && (
                    <HStack w={'full'} px={4}>
                        <Text color={'purple.300'} fontSize={13} fontWeight={500}>
                            Suma maksymalnych punktów kryteriów scenariusza: {scenarioMaxScore} pkt
                        </Text>
                        <Text color={'text.grey'} fontSize={12}>
                            (nie musi to być finalna suma -- model może zostać uzupełniony lub nadpisany podczas tworzenia rekrutacji)
                        </Text>
                    </HStack>
                )}
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={intents.map(i => i.id)} strategy={verticalListSortingStrategy}>
                        {intents.map((intent) => (
                            <IntentListItem intent={intent} key={intent.id} onClick={() => handleIntentClick(intent)}/>
                        ))}
                    </SortableContext>
                </DndContext>
            </VStack>
            <HStack w={'100%'} px={4} py={4} justifyContent={'space-between'}>
                <HStack gap={4}>
                    <ConfirmationButton variant="outlined" textDecoration='unset' icon={<HiOutlineTrash/>}
                                        label={"Delete scenario"} onConfirm={handleDeleteScenario} isLoading={isLoading}/>

                    <ConfirmationButton variant="outlined" textDecoration='unset' icon={<HiOutlineDocumentDuplicate/>}
                                        label={"Duplicate scenario"} onConfirm={handleDuplicateScenario} withoutConfirmation isLoading={isLoading}/>

                    <Button variant="outlined" textDecoration='unset' onClick={handleExportScenario}>
                        <Icon fontSize={18}>
                            <HiOutlineArrowDownTray/>
                        </Icon>
                        <Text>Export scenario</Text>
                    </Button>
                </HStack>
                <HStack gap={6}>
                    <Button variant="outlined" onClick={handleAddNewIntent} textDecoration='unset'>
                        <Icon fontSize={46}>
                            <HiOutlinePlus/>
                        </Icon>
                        <Text>Add new intent</Text>
                    </Button>
                    <Button variant="primary" textDecoration='unset'>
                        <Text>Save scenario</Text>
                    </Button>
                </HStack>
            </HStack>
            <CreateIntentDrawer isOpen={isDrawerOpen}
                                setOpen={setIsDrawerOpen}
                                selectedIntent={selectedIntent}
                                scenario={scenario}
                                isLoading={isWorking}
                                onSave={handleOnSave}
                                onDelete={handleIntentRemove}
            />
        </VStack>
    )
}

export default ScenarioPage;
